import ResourceTyped from 'Resources/ResourceTyped';
import NotificationService from 'App/babel/NotificationService';

export type AgentMemory = {
	id: number;
	agentId: number;
	userId: number;
	type: string;
	key: string;
	value: string;
	source: string;
	active: boolean;
	regDate: string;
	modDate: string;
};

export type CustomerKnowledge = {
	id: number;
	category: string;
	key: string;
	value: string;
	confidence: number;
	usageCount: number;
};

export type AgentStatus = 'active' | 'paused' | 'archived' | 'draft';
export type AgentVisibility = 'org' | 'personal';
export type AutonomyDisposition = 'auto' | 'propose';
export type PermissionLevel = 'read' | 'modify' | 'create' | 'delete';
// Mirrors CAPABILITIES in base's autonomyEvaluator — a capability missing here
// used to be a grant with no row and no off switch (WEB-11381).
export type Capability = 'send_email' | 'manage_automations' | 'manage_agents' | 'delegate_task' | 'manage_memory';
export type AutonomyBoundaries = Record<string, AutonomyDisposition | PermissionLevel | boolean>;

export type Agent = {
	id: number;
	name: string;
	visibility: AgentVisibility;
	ownerUserId: number | null;
	status: AgentStatus;
	role: string | null;
	adapterType: string;
	adapterConfig?: Record<string, unknown> | null;
	runtimeConfig?: Record<string, unknown> | null;
	autonomyLevel: number;
	autonomyBoundaries?: AutonomyBoundaries | null;
	defaultActorUserId: number | null;
	canAutoApproveSubordinates: boolean;
	budgetMonthlySek: number;
	spentMonthlyCents: number;
	systemPromptOverride: string | null;
	capabilities?: Record<string, unknown> | null;
	trustMetrics?: Record<string, unknown> | null;
	avatarUrl: string | null;
	reportsTo: number | null;
	// Who approves an org agent's proposals (WEB-12796). Both empty = the run's user.
	approverUsers?: number[];
	approverRoles?: number[];
	promotedFromAgentId: number | null;
	promotedFromRevisionId: number | null;
	createdByUserId: number | null;
	regDate: string;
	modDate: string;
	lastRunAt: string | null;
	// Set when the agent was instantiated from a master-authored template.
	sourceTemplateId: number | null;
	sourceTemplateRevisionId: number | null;
	templateActive: boolean | null;
	// Template this agent was instantiated from (e.g. 'outreach-agent-assistant').
	templateKey: string | null;
};

export type CreateAgentInput = {
	name?: string;
	visibility?: AgentVisibility;
	role?: string;
	adapterType?: string;
	systemPromptOverride?: string | null;
	reportsTo?: number | null;
	defaultActorUserId?: number | null;
	autonomyLevel?: number;
	budgetMonthlySek?: number;
};

export type PromotionEligibility = {
	eligible: boolean;
	completedTasks: number;
	succeededTasks: number;
	successRate: number;
	requiredTasks: number;
	requiredSuccessRate: number;
	alreadyRequested: boolean;
	agentName: string;
	agentRole: string | null;
};

export type CrmAction = 'update' | 'create' | 'delete';

export type PermissionUpgradeEligibility = {
	entity: string;
	action: CrmAction;
	consecutiveCleanApprovals: number;
	requiredApprovals: number;
	eligible: boolean;
};

export type CapabilityUpgradeEligibility = {
	capability: Capability;
	consecutiveCleanApprovals: number;
	requiredApprovals: number;
	eligible: boolean;
};

export type UpgradeEligibilityResult = {
	entityUpgrades: PermissionUpgradeEligibility[];
	capabilityUpgrades: CapabilityUpgradeEligibility[];
	/**
	 * Every entity the autonomy gate can require a grant on, derived server-side
	 * from the same specs the evaluator uses (WEB-11381). The permissions tab's
	 * grant picker is built from this — a hand-kept copy drifted, leaving live
	 * grants with no row and no way to switch them off.
	 */
	grantableEntities: string[];
};

export type DreamConfig = {
	enabled?: boolean;
};

export type UpdateAgentInput = Partial<{
	name: string;
	// Only the one-way personal -> org flip is accepted by the API.
	visibility: AgentVisibility;
	role: string;
	avatarUrl: string | null;
	systemPromptOverride: string | null;
	autonomyLevel: number;
	budgetMonthlySek: number;
	reportsTo: number | null;
	canAutoApproveSubordinates: boolean;
	autonomyBoundaries: AutonomyBoundaries | null;
	defaultActorUserId: number | null;
	approverUsers: number[];
	approverRoles: number[];
	reason: string;
	dreamConfig: DreamConfig;
	templateActive: boolean;
}>;

class Agents extends ResourceTyped<Agent> {
	constructor() {
		super('agents');
	}

	async list(filters: { visibility?: AgentVisibility } = {}): Promise<Agent[]> {
		const params = new URLSearchParams();
		if (filters.visibility) params.set('visibility', filters.visibility);
		const q = params.toString();
		const res = await fetch(`/api/v2/orchestration/agents${q ? `?${q}` : ''}`);
		if (!res.ok) {
			const fallback = await fetch(`/api/v2/agents${q ? `?${q}` : ''}`);
			if (!fallback.ok) throw new Error(`Failed to list agents (${fallback.status})`);
			const body = await fallback.json();
			return body.data ?? [];
		}
		const body = await res.json();
		return body.data ?? [];
	}

	async listArchived(): Promise<Agent[]> {
		const res = await fetch('/api/v2/orchestration/agents?status=archived');
		if (!res.ok) return [];
		const body = await res.json();
		return body.data ?? [];
	}

	async getOne(id: number): Promise<Agent> {
		const res = await fetch(`/api/v2/agents/${id}`);
		if (!res.ok) throw new Error(`Failed to load agent (${res.status})`);
		const body = await res.json();
		return body.data;
	}

	async create(input: CreateAgentInput): Promise<{ id: number }> {
		const res = await fetch('/api/v2/orchestration/agents', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(input)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.translated || body?.error?.msg || `Failed to create agent (${res.status})`);
		}
		const body = await res.json();
		return body.data;
	}

	async updateOne(id: number, input: UpdateAgentInput): Promise<Agent> {
		const res = await fetch(`/api/v2/orchestration/agents/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(input)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.translated || body?.error?.msg || `Failed to update agent (${res.status})`);
		}
		const body = await res.json();
		return body.data;
	}

	async resume(id: number): Promise<Agent> {
		return this.transitionStatus(id, 'resume');
	}

	async archive(id: number): Promise<Agent> {
		return this.transitionStatus(id, 'archive');
	}

	async getMemories(agentId: number): Promise<AgentMemory[]> {
		const res = await fetch(`/api/v2/agents/${agentId}/memory`);
		if (!res.ok) throw new Error(`Failed to load memories (${res.status})`);
		const body = await res.json();
		return body.data ?? [];
	}

	async updateMemory(agentId: number, memoryId: number, value: string): Promise<void> {
		const res = await fetch(`/api/v2/agents/${agentId}/memory/${memoryId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ value })
		});
		if (!res.ok) throw new Error(`Failed to update memory (${res.status})`);
	}

	async deleteMemory(agentId: number, memoryId: number): Promise<void> {
		const res = await fetch(`/api/v2/agents/${agentId}/memory/${memoryId}`, { method: 'DELETE' });
		if (!res.ok) throw new Error(`Failed to delete memory (${res.status})`);
	}

	async getCustomerKnowledge(agentId: number): Promise<CustomerKnowledge[]> {
		const res = await fetch(`/api/v2/agents/${agentId}/customer-knowledge`);
		if (!res.ok) throw new Error(`Failed to load knowledge (${res.status})`);
		const body = await res.json();
		return body.data ?? [];
	}

	async resetAgent(agentId: number): Promise<void> {
		const res = await fetch(`/api/v2/agents/${agentId}/reset`, { method: 'POST' });
		if (!res.ok) {
			NotificationService.add({
				icon: 'times',
				style: NotificationService.style.ERROR,
				title: 'assistant.resetFailed'
			});
			throw new Error(`Failed to reset agent (${res.status})`);
		}
		NotificationService.add({
			icon: 'check',
			style: NotificationService.style.SUCCESS,
			title: 'assistant.resetDone',
			body: 'assistant.resetDoneSub'
		});
	}

	async getPromotionEligibility(agentId: number): Promise<PromotionEligibility> {
		const res = await fetch(`/api/v2/assistant/agents/${agentId}/promotion-eligibility`);
		if (!res.ok) throw new Error(`Failed to check eligibility (${res.status})`);
		const body = await res.json();
		return body.data;
	}

	async getPermissionUpgradeEligibility(agentId: number): Promise<UpgradeEligibilityResult> {
		const res = await fetch(`/api/v2/assistant/agents/${agentId}/permission-upgrade-eligibility`);
		if (!res.ok) throw new Error(`Failed to check upgrade eligibility (${res.status})`);
		const body = await res.json();
		return body.data;
	}

	private async transitionStatus(id: number, action: 'resume' | 'archive'): Promise<Agent> {
		const res = await fetch(`/api/v2/agents/${id}/${action}`, { method: 'POST' });
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.translated || body?.error?.msg || `Failed to ${action} agent (${res.status})`);
		}
		const body = await res.json();
		return body.data;
	}
}

const resource = new Agents();
export default resource;
